import * as THREE from 'three'

interface PropsOrgaosTronco {
  alturaTronco: number
  profundidadeTronco: number
  larguraTronco: number
  intensidadePulso?: number
}

interface PropsCerebro {
  raioCabeca: number
  intensidadePulso?: number
}

/**
 * Encéfalo tridimensional com sinalização de Coup / Contrecoup (golpe e contragolpe)
 * para a 3ª Colisão do Trauma no interior do crânio.
 */
export function Cerebro3D({ raioCabeca, intensidadePulso = 0.8 }: PropsCerebro) {
  const escalaCerebro = raioCabeca * 0.72

  return (
    <group position={[0, 0, 0]}>
      {/* Hemisfério Esquerdo */}
      <mesh position={[-escalaCerebro * 0.32, 0, 0]} scale={[0.78, 0.95, 1.05]}>
        <sphereGeometry args={[escalaCerebro * 0.65, 16, 14]} />
        <meshStandardMaterial
          color="#f43f5e"
          emissive="#be123c"
          emissiveIntensity={intensidadePulso}
          roughness={0.4}
        />
      </mesh>

      {/* Hemisfério Direito */}
      <mesh position={[escalaCerebro * 0.32, 0, 0]} scale={[0.78, 0.95, 1.05]}>
        <sphereGeometry args={[escalaCerebro * 0.65, 16, 14]} />
        <meshStandardMaterial
          color="#f43f5e"
          emissive="#be123c"
          emissiveIntensity={intensidadePulso}
          roughness={0.4}
        />
      </mesh>

      {/* Cerebelo posterior/inferior */}
      <mesh position={[0, -escalaCerebro * 0.42, -escalaCerebro * 0.36]} scale={[0.85, 0.6, 0.7]}>
        <sphereGeometry args={[escalaCerebro * 0.45, 12, 10]} />
        <meshStandardMaterial
          color="#e11d48"
          emissive="#9f1239"
          emissiveIntensity={intensidadePulso * 0.8}
          roughness={0.5}
        />
      </mesh>

      {/* Halo de Coup / Impacto Frontal */}
      <mesh position={[0, escalaCerebro * 0.1, escalaCerebro * 0.62]} rotation={[0, 0, 0]}>
        <ringGeometry args={[escalaCerebro * 0.25, escalaCerebro * 0.45, 16]} />
        <meshBasicMaterial color="#fbbf24" transparent opacity={0.85} side={THREE.DoubleSide} />
      </mesh>

      {/* Halo de Contrecoup / Contragolpe Occipital */}
      <mesh position={[0, 0, -escalaCerebro * 0.62]} rotation={[0, Math.PI, 0]}>
        <ringGeometry args={[escalaCerebro * 0.28, escalaCerebro * 0.5, 16]} />
        <meshBasicMaterial color="#ef4444" transparent opacity={0.9} side={THREE.DoubleSide} />
      </mesh>
    </group>
  )
}

/**
 * Órgãos vitais torácicos e abdominais para visualização em Raio-X da 3ª Colisão do Trauma:
 * - Coração e Cajado da Aorta (com anel de cisalhamento no ligamento arterioso)
 * - Pulmões (com zona de contusão pulmonar e efeito paper bag)
 * - Fígado e Baço (órgãos sólidos subdiafragmáticos com risco de hemorragia oculta)
 */
export function OrgaosTronco3D({
  alturaTronco,
  larguraTronco,
  profundidadeTronco,
  intensidadePulso = 0.85,
}: PropsOrgaosTronco) {
  const yCoracao = alturaTronco * 0.60
  const yPulmoes = alturaTronco * 0.62
  const yOrgaosAbdominais = alturaTronco * 0.26

  return (
    <group position={[0, 0, 0]}>
      {/* 1. PULMÕES (Bilateral translúcido com zonas de contusão pulmonar) */}
      {/* Pulmão Direito */}
      <group position={[larguraTronco * 0.26, yPulmoes, 0]}>
        <mesh scale={[0.85, 1.35, 0.75]}>
          <capsuleGeometry args={[larguraTronco * 0.15, alturaTronco * 0.14, 10, 14]} />
          <meshStandardMaterial
            color="#38bdf8"
            emissive="#0284c7"
            emissiveIntensity={0.35}
            transparent
            opacity={0.62}
            roughness={0.35}
          />
        </mesh>
        {/* Zona de contusão torácica / atelectasia por compressão */}
        <mesh position={[0, -alturaTronco * 0.05, profundidadeTronco * 0.22]}>
          <sphereGeometry args={[larguraTronco * 0.09, 10, 10]} />
          <meshStandardMaterial color="#a855f7" emissive="#7e22ce" emissiveIntensity={0.7} />
        </mesh>
      </group>

      {/* Pulmão Esquerdo (com incisura cardíaca) */}
      <group position={[-larguraTronco * 0.26, yPulmoes, 0]}>
        <mesh scale={[0.78, 1.32, 0.72]}>
          <capsuleGeometry args={[larguraTronco * 0.14, alturaTronco * 0.13, 10, 14]} />
          <meshStandardMaterial
            color="#38bdf8"
            emissive="#0284c7"
            emissiveIntensity={0.35}
            transparent
            opacity={0.62}
            roughness={0.35}
          />
        </mesh>
        {/* Zona de contusão torácica homolateral */}
        <mesh position={[0, -alturaTronco * 0.04, profundidadeTronco * 0.22]}>
          <sphereGeometry args={[larguraTronco * 0.08, 10, 10]} />
          <meshStandardMaterial color="#a855f7" emissive="#7e22ce" emissiveIntensity={0.7} />
        </mesh>
      </group>

      {/* 2. CORAÇÃO & ARCO AÓRTICO */}
      <group position={[-larguraTronco * 0.04, yCoracao, profundidadeTronco * 0.12]}>
        {/* Massa miocárdica */}
        <mesh rotation={[0.1, 0, 0.25]} scale={[0.9, 1.15, 0.85]}>
          <sphereGeometry args={[alturaTronco * 0.11, 14, 14]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#b91c1c"
            emissiveIntensity={intensidadePulso}
            roughness={0.3}
          />
        </mesh>

        {/* Cajado / Arco da Aorta (Emergência superior e curvatura para trás) */}
        <mesh position={[0.015, alturaTronco * 0.11, -0.01]} rotation={[Math.PI / 2 - 0.2, 0.3, 0]}>
          <torusGeometry args={[alturaTronco * 0.065, alturaTronco * 0.022, 12, 24, Math.PI * 1.1]} />
          <meshStandardMaterial
            color="#dc2626"
            emissive="#991b1b"
            emissiveIntensity={intensidadePulso * 1.1}
            roughness={0.25}
          />
        </mesh>

        {/* Anel de Cisalhamento no Ligamento Arterioso (Istmo Aórtico) */}
        <mesh position={[-0.01, alturaTronco * 0.15, -0.04]} rotation={[0, Math.PI / 4, 0]}>
          <torusGeometry args={[alturaTronco * 0.038, alturaTronco * 0.008, 8, 16]} />
          <meshBasicMaterial color="#f59e0b" transparent opacity={0.95} />
        </mesh>
      </group>

      {/* 3. FÍGADO (Hipocôndrio Direito - Grande órgão sólido em risco) */}
      <group position={[larguraTronco * 0.16, yOrgaosAbdominais + alturaTronco * 0.06, 0.01]}>
        <mesh rotation={[0, 0.1, -0.2]} scale={[1.3, 0.75, 0.95]}>
          <sphereGeometry args={[larguraTronco * 0.16, 14, 12]} />
          <meshStandardMaterial
            color="#ea580c"
            emissive="#c2410c"
            emissiveIntensity={intensidadePulso * 0.7}
            roughness={0.45}
          />
        </mesh>
        {/* Alerta de compressão hepática */}
        <mesh position={[larguraTronco * 0.08, 0, profundidadeTronco * 0.18]}>
          <ringGeometry args={[0.02, 0.04, 12]} />
          <meshBasicMaterial color="#f59e0b" transparent opacity={0.85} side={THREE.DoubleSide} />
        </mesh>
      </group>

      {/* 4. BAÇO (Hipocôndrio Esquerdo - Órgão ricamente vascularizado) */}
      <group position={[-larguraTronco * 0.22, yOrgaosAbdominais + alturaTronco * 0.08, -0.01]}>
        <mesh rotation={[0.2, -0.15, 0.3]} scale={[0.8, 1.15, 0.65]}>
          <sphereGeometry args={[larguraTronco * 0.09, 12, 10]} />
          <meshStandardMaterial
            color="#b45309"
            emissive="#92400e"
            emissiveIntensity={intensidadePulso * 0.75}
            roughness={0.45}
          />
        </mesh>
        {/* Alerta de desgarro pedicular / laceração esplênica */}
        <mesh position={[-larguraTronco * 0.04, 0, profundidadeTronco * 0.16]}>
          <ringGeometry args={[0.018, 0.038, 12]} />
          <meshBasicMaterial color="#ef4444" transparent opacity={0.9} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  )
}
